import MenuIcon from '@mui/icons-material/Menu';
import {
  AppBar,
  Box,
  Button,
  Container,
  IconButton,
  Menu,
  MenuItem,
  Toolbar,
  Tooltip,
  Typography,
} from '@mui/material';
import React from 'react';
import {
  Outlet,
  useLocation,
  useNavigate,
  useSearchParams,
} from 'react-router-dom';
import { routes } from '../../routes';
import { AppRoute } from '../../types';
import { isAuthenticated } from '../../utils/legacyauth';
import userServices from '../../utils/user';
import { useMe } from '../../config/api/queries';
import { colors } from '../../utils/theme';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';

const pages: AppRoute[] = routes.filter((route) => route.primary === true);
const settings: AppRoute[] = routes.filter((route) => route.primary === false);

export const Navigation = (): JSX.Element => {
  const me = useMe();
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams, _] = useSearchParams();
  const isUsingApp = searchParams.get('mobile');
  const [anchorElNav, setAnchorElNav] = React.useState<null | HTMLElement>(
    null
  );
  const [anchorElUser, setAnchorElUser] = React.useState<null | HTMLElement>(
    null
  );
  const [anchorElUserXs, setAnchorElUserXs] =
    React.useState<null | HTMLElement>(null);

  const username: string = userServices.getName();

  const handleOpenNavMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorElNav(event.currentTarget);
  };
  const handleOpenUserMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorElUser(event.currentTarget);
  };
  const handleOpenUserMenuXs = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorElUserXs(event.currentTarget);
  };

  const handleCloseNavMenu = () => {
    setAnchorElNav(null);
  };

  const handleCloseUserMenu = () => {
    setAnchorElUser(null);
  };

  const handleCloseUserMenuXs = () => {
    setAnchorElUserXs(null);
  };

  const handleLogout = () => {
    userServices.logOut();
    navigate('/login');
  };

  const NavMenu = ({
    id,
    anchor,
    onClose,
    routes,
    showLogout = false,
    sx,
  }: {
    id: string;
    anchor: HTMLElement | null;
    onClose: () => void;
    routes: AppRoute[];
    showLogout?: boolean;
    sx?: any;
  }) => {
    return (
      <Menu
        id={id}
        anchorEl={anchor}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        keepMounted
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        open={Boolean(anchor)}
        onClose={onClose}
        disableScrollLock={true}
        sx={sx}
      >
        {routes.map((route) => (
          <MenuItem
            key={route.path}
            onClick={() => {
              onClose();
              return route.external
                ? (window.location.href = route.path)
                : navigate(route.path);
            }}
          >
            <Typography textAlign="center" sx={{ fontSize: 12 }}>
              {route.title}
            </Typography>
          </MenuItem>
        ))}
        {showLogout && (
          <MenuItem onClick={handleLogout}>
            <Typography textAlign="center" sx={{ fontSize: 12 }}>
              Logout
            </Typography>
          </MenuItem>
        )}
      </Menu>
    );
  };

  return isAuthenticated() && location.pathname !== '/admin' && !isUsingApp ? (
    <>
      <AppBar
        position="fixed"
        sx={{
          background: colors.greys.BASE_WHITE,
          boxShadow: 'none',
          borderBottom: `1px solid ${colors.greys.BORDER_GREY}`,
        }}
      >
        <Container maxWidth={false} sx={{ pl: 0, pr: 0 }}>
          <Toolbar disableGutters={true}>
            <IconButton
              size="small"
              aria-label="Rootine logo - navigate to dashboard"
              aria-controls="menu-appbar"
              onClick={() => navigate('/dashboard')}
              color="inherit"
              sx={{
                display: { xs: 'none', md: 'flex' },
                '&:hover': { background: 'transparent' },
              }}
            >
              <img
                src="/images/logo_black.svg"
                alt="logo"
                style={{ width: 90 }}
              />
            </IconButton>

            <Box
              sx={{
                display: { xs: 'flex', md: 'none' },
                flexDirection: { xs: 'row' },
                justifyContent: 'space-between',
                width: '100%',
              }}
            >
              <Box
                sx={{
                  display: { xs: 'flex', md: 'none' },
                  alignItems: 'center',
                  px: 1,
                  cursor: 'pointer',
                }}
                onClick={() => navigate('/dashboard')}
              >
                <img
                  style={{
                    height: 30,
                  }}
                  src="/images/logo_compact_black.svg"
                  alt="logo"
                />
              </Box>

              <Box
                sx={{
                  display: { xs: 'flex', md: 'none' },
                  flexDirection: 'row',
                }}
              >
                <Box>
                  <IconButton
                    size="large"
                    aria-label="account of current user"
                    aria-controls="menu-appbar"
                    aria-haspopup="true"
                    onClick={handleOpenNavMenu}
                    color="inherit"
                    sx={{ color: colors.greys.BRAND_BLACK }}
                  >
                    <MenuIcon />
                  </IconButton>
                  <NavMenu
                    id="menu-appbar"
                    anchor={anchorElNav}
                    onClose={handleCloseNavMenu}
                    routes={pages}
                  />
                </Box>

                <Box>
                  <Tooltip title="Open settings">
                    <IconButton onClick={handleOpenUserMenuXs}>
                      <AccountCircleIcon
                        fontSize="large"
                        sx={{ color: colors.greys.BRAND_BLACK }}
                      />
                    </IconButton>
                  </Tooltip>
                  <NavMenu
                    id="menu-appbar-user-xs"
                    anchor={anchorElUserXs}
                    onClose={handleCloseUserMenuXs}
                    routes={settings}
                    showLogout={true}
                  />
                </Box>
              </Box>
            </Box>
            <Box
              component="span"
              sx={{
                flexGrow: 1,
                justifyContent: 'flex-end',
                display: { xs: 'none', md: 'flex' },
              }}
            >
              {pages.map((page: AppRoute) => (
                <Box
                  key={page.title}
                  component="span"
                  sx={{
                    position: 'relative',
                    cursor: 'pointer',
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      bottom: 0,
                      left: 0,
                      right: 0,
                      height: '1px',
                      backgroundColor: colors.greys.BRAND_BLACK,
                      transform: 'scaleX(0)',
                      transformOrigin: 'center',
                      transition: 'transform 0.3s ease',
                    },
                    '&:hover::before': {
                      transform: 'scaleX(1)',
                    },
                  }}
                  onClick={() =>
                    page.external
                      ? (window.location.href = page.path)
                      : navigate(page.path)
                  }
                >
                  <Button
                    sx={{
                      my: 2,
                      display: 'block',
                      '&:hover': { background: 'transparent' },
                    }}
                  >
                    <Typography
                      sx={{ fontSize: 14, color: colors.greys.BRAND_BLACK }}
                    >
                      {page.title}
                    </Typography>
                  </Button>
                </Box>
              ))}
              {me?.data?.customer_type === 'prime4' && (
                <Box
                  component="span"
                  sx={{
                    position: 'relative',
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      bottom: 0,
                      left: 0,
                      right: 0,
                      height: '1px',
                      backgroundColor: colors.greys.BRAND_BLACK,
                      transform: 'scaleX(0)',
                      transformOrigin: 'center',
                      transition: 'transform 0.3s ease',
                    },
                    '&:hover::before': {
                      transform: 'scaleX(1)',
                    },
                  }}
                >
                  <Button
                    href="https://my.rootine.co/user2/summary-print-prime4"
                    sx={{
                      my: 2,
                      display: 'block',
                      '&:hover': { background: 'transparent' },
                    }}
                  >
                    <Typography
                      sx={{ fontSize: 14, color: colors.greys.BRAND_BLACK }}
                    >
                      Download Prime IV Report
                    </Typography>
                  </Button>
                </Box>
              )}
            </Box>

            <Box sx={{ flexGrow: 0, display: { xs: 'none', md: 'flex' } }}>
              <Tooltip title="Open settings">
                <IconButton onClick={handleOpenUserMenu}>
                  <AccountCircleIcon
                    fontSize="large"
                    sx={{ color: colors.greys.BRAND_BLACK }}
                  />
                </IconButton>
              </Tooltip>
              <NavMenu
                id="menu-appbar-user"
                anchor={anchorElUser}
                onClose={handleCloseUserMenu}
                routes={settings}
                showLogout={true}
                sx={{
                  mt: 0.5,
                  position: 'fixed',
                }}
              />
            </Box>
          </Toolbar>
        </Container>
      </AppBar>
      <Outlet />
    </>
  ) : (
    <Outlet />
  );
};
